import { useRef, useEffect } from 'react';

/**
 * ItemTabs：赛项横向可拖拽 Tab 标签条
 * props:
 *   items      - 赛项列表，每项须含 id / name / groupName（/ apply 可选）
 *   activeType - 当前选中的赛项对象
 *   onChange   - 切换 tab 时的回调，参数为被点击的赛项对象
 *   showApply  - 是否在 tab 右下角显示报名人数（默认 false，仅 Players 页面传 true）
 */
export default function ItemTabs({
  items = [],
  activeType,
  onChange = () => {},
  showApply = false,
}) {
  // refs for drag handling
  const containerRef = useRef(null);
  const isPointerDownRef = useRef(false);
  const startXRef = useRef(0);
  const startScrollLeftRef = useRef(0);
  const movedRef = useRef(false);

  useEffect(() => {
    if (items.length > 0 && !activeType) {
      onChange(items[0]);
    }
  }, [items, activeType, onChange]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      // 检测是否为鼠标滚轮：deltaY 绝对值大于等于 100
      const isWheel = Math.abs(e.deltaY) >= 100;

      if (isWheel) {
        // 鼠标滚轮：阻止默认行为并实现水平滚动
        e.preventDefault();
        container.scrollLeft += e.deltaY;
      }
      // 触控板/触摸板：让浏览器默认处理（自然滚动）
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // 鼠标按住拖动（改用 mouse 事件以避免 pointer capture 导致 click 失效）
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const setGrab = () => container.classList.add('cursor-grab');
    const setGrabbing = () => {
      container.classList.remove('cursor-grab');
      container.classList.add('cursor-grabbing');
    };
    const clearCursor = () => {
      container.classList.remove('cursor-grab');
      container.classList.remove('cursor-grabbing');
    };

    setGrab();

    const onMouseDown = (e) => {
      // 仅响应左键
      if (e.button !== 0) return;
      isPointerDownRef.current = true;
      movedRef.current = false;
      startXRef.current = e.clientX;
      startScrollLeftRef.current = container.scrollLeft;

      container.style.userSelect = 'none';
      setGrabbing();
    };

    const onMouseMove = (e) => {
      if (!isPointerDownRef.current) return;
      const dx = e.clientX - startXRef.current;
      if (Math.abs(dx) > 5) {
        movedRef.current = true;
      }
      container.scrollLeft = startScrollLeftRef.current - dx;
    };

    const onMouseUpOrLeave = () => {
      if (!isPointerDownRef.current) return;
      isPointerDownRef.current = false;
      container.style.userSelect = '';
      clearCursor();
      // 保持 moved 状态直到 click 事件完成（短延迟）
      setTimeout(() => {
        movedRef.current = false;
      }, 0);
    };

    container.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUpOrLeave);
    container.addEventListener('mouseleave', onMouseUpOrLeave);

    return () => {
      clearCursor();
      container.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUpOrLeave);
      container.removeEventListener('mouseleave', onMouseUpOrLeave);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="flex gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
    >
      {items.map((t) => (
        <button
          key={t.id}
          onClick={(e) => {
            // 如果刚才发生了拖动，则抑制 click
            if (movedRef.current) {
              e.preventDefault();
              e.stopPropagation();
              movedRef.current = false;
              return;
            }
            onChange(t);
          }}
          className={`group relative flex-shrink-0 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer border-2 ${
            activeType?.id === t.id
              ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-600/25'
              : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 hover:text-blue-700'
          }`}
        >
          <div className="flex flex-col items-start gap-0.5 w-full">
            <span className="font-semibold">{t.name}</span>
            <span
              className={`text-xs ${
                activeType?.id === t.id
                  ? 'text-blue-100'
                  : 'text-slate-500 group-hover:text-blue-600'
              }`}
            >
              {t.groupName}
            </span>
            {/* 仅当父组件传入 showApply=true 时，在第三行靠右显示报名人数；单位根据 contestType 动态显示：1=人，2=队 */}
            {showApply && (
              <span
                className={`text-xs self-end ${
                  activeType?.id === t.id
                    ? 'text-blue-200'
                    : 'text-slate-400 group-hover:text-blue-500'
                }`}
              >
                {t.apply ?? 0}{t.contestType === 2 ? '队' : '人'}
              </span>
            )}
          </div>

          {/* Active indicator */}
          {activeType?.id === t.id && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm" />
          )}
        </button>
      ))}
    </div>
  );
}
